/**
 * Give the child on a shared account their own login.
 *
 * A shared account is one login, keyed on the parent's email, that carries the
 * child's profile nested inside it. Coach bulk upload creates one for any row
 * with a parent email but no child email, so parents routinely end up sharing
 * an account they never chose to share.
 *
 * Splitting converts the existing record into the child's account and creates a
 * new one for the parent. The parent keeps the same credentials and the same
 * session -- the Cognito identity moves to their new record -- so there is no
 * sign-out here. What does change is their role: they stop being a learner and
 * become a guardian, so the caller swaps the cached profile and persona.
 */
import React, { useState } from "react";
import { CircularProgress, Dialog, Icon, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import palette from "assets/theme/base/palette";
import { call } from "services/axiosService/core";
import * as Sentry from "@sentry/browser";

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fafafa" } };

const firstWord = (value) => (value || "").trim().split(/\s+/)[0] || "";
const restOfName = (value) => (value || "").trim().split(/\s+/).slice(1).join(" ");

const Bullet = ({ icon, color, children }) => (
  <MDBox display="flex" alignItems="flex-start" gap={1.5} mb={1.75}>
    <Icon sx={{ fontSize: "20px !important", color, flexShrink: 0, mt: 0.25 }}>{icon}</Icon>
    <MDTypography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
      {children}
    </MDTypography>
  </MDBox>
);

const Field = ({ label, value, onChange, type = "text", placeholder, required = true }) => (
  <MDBox mb={2.25}>
    <MDTypography variant="body2" fontWeight="bold" mb={0.75}>
      {label} {required && <span style={{ color: "#f44336" }}>*</span>}
    </MDTypography>
    <TextField
      fullWidth
      size="small"
      type={type}
      value={value}
      placeholder={placeholder}
      required={required}
      sx={fieldSx}
      onChange={(e) => onChange(e.target.value)}
    />
  </MDBox>
);

const SplitSharedAccount = ({ open, onClose, profile, onSplit, t }) => {
  const learnerDetails = profile?.details?.learner_details || {};
  const childName = learnerDetails.name || "";

  const [step, setStep] = useState("intro");
  const [childFirstName, setChildFirstName] = useState(firstWord(childName));
  const [childLastName, setChildLastName] = useState(restOfName(childName));
  const [childEmail, setChildEmail] = useState("");
  const [parentFirstName, setParentFirstName] = useState(firstWord(profile?.details?.name));
  const [parentLastName, setParentLastName] = useState(restOfName(profile?.details?.name));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);

  const close = () => {
    if (submitting) return;
    onClose();
  };

  const goToConfirm = (e) => {
    e.preventDefault();
    setError("");
    setStep("confirm");
  };

  const submit = () => {
    setSubmitting(true);
    setError("");
    call(`users/${profile.id}/split-shared-account`, "PUT", {
      body: {
        child_email: childEmail.trim().toLowerCase(),
        child_first_name: childFirstName.trim(),
        child_last_name: childLastName.trim(),
        parent_first_name: parentFirstName.trim(),
        parent_last_name: parentLastName.trim(),
      },
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => {
        setSubmitting(false);
        if (response.status === 200) {
          setWarnings(response.data?.warnings || []);
          setStep("done");
          // The caller swaps the cached profile for the new parent record and
          // moves the persona to guardian. The session itself keeps working.
          onSplit(response.data);
          return;
        }
        setStep("form");
        setError(
          response.data?.message ||
            t("splitAccount.genericError") ||
            "We could not split this account. Please try again."
        );
      })
      .catch((err) => {
        Sentry.captureException(err);
        setSubmitting(false);
        setStep("form");
        setError(
          t("splitAccount.genericError") ||
            "We could not split this account. Please try again."
        );
      });
  };

  const displayChildName = `${childFirstName} ${childLastName}`.trim() || childName;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
    >
      {!submitting && (
        <MDBox
          onClick={close}
          sx={{
            position: "absolute", top: 12, right: 12, zIndex: 10,
            width: 36, height: 36, borderRadius: "50%",
            bgcolor: palette.neutral[100], border: `1px solid ${palette.neutral[200]}`,
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            "&:hover": { bgcolor: palette.neutral[200] },
          }}
        >
          <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
        </MDBox>
      )}

      <MDBox sx={{ px: { xs: 3, sm: 4 }, pt: 5, pb: 4 }}>
        <MDBox textAlign="center" mb={3}>
          <MDBox
            sx={{
              width: 80, height: 80, borderRadius: "50%",
              bgcolor: step === "done" ? "#4caf5018" : palette.brand[50],
              display: "flex", alignItems: "center", justifyContent: "center",
              mx: "auto", mb: 2,
            }}
          >
            <Icon
              sx={{
                fontSize: "40px !important",
                color: step === "done" ? "#4caf50" : palette.brand[500],
              }}
            >
              {step === "done" ? "check_circle" : "call_split"}
            </Icon>
          </MDBox>
          <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
            {step === "done"
              ? t("splitAccount.successTitle")
              : t("splitAccount.title")}
          </MDTypography>
        </MDBox>

        {/* ── Step 1: what this does ── */}
        {step === "intro" && (
          <>
            <MDTypography variant="body2" color="text.secondary" mb={2.5} sx={{ lineHeight: 1.7 }}>
              {t("splitAccount.introBody", { childName: childName || t("splitAccount.yourChild") })}
            </MDTypography>
            <Bullet icon="school" color={palette.brand[500]}>
              {t("splitAccount.pointChildKeepsResults")}
            </Bullet>
            <Bullet icon="groups" color={palette.brand[500]}>
              {t("splitAccount.pointCoachKeepsAccess")}
            </Bullet>
            <Bullet icon="login" color={palette.brand[500]}>
              {t("splitAccount.pointYouStaySignedIn")}
            </Bullet>
            <Bullet icon="block" color="#ff9800">
              {t("splitAccount.pointNoMoreAssessments")}
            </Bullet>

            <MDBox display="flex" gap={2} mt={3.5}>
              <MDButton
                variant="outlined"
                fullWidth
                onClick={close}
                sx={{
                  borderRadius: "12px", py: 1.5, textTransform: "none",
                  borderColor: palette.neutral[300], color: palette.neutral[600], fontWeight: "bold",
                }}
              >
                {t("buttonTitles.cancel") || "Cancel"}
              </MDButton>
              <MDButton
                variant="contained"
                fullWidth
                onClick={() => setStep("form")}
                sx={{
                  borderRadius: "12px", py: 1.5, textTransform: "none",
                  bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold",
                  "&:hover": { bgcolor: palette.brand[600] },
                }}
              >
                {t("buttonTitles.continue") || "Continue"}
              </MDButton>
            </MDBox>
          </>
        )}

        {/* ── Step 2: details ── */}
        {step === "form" && (
          <form onSubmit={goToConfirm}>
            {error && (
              <MDBox
                mb={2.5}
                sx={{
                  p: 1.75, borderRadius: "12px",
                  bgcolor: "#f4433612", border: "1px solid #f4433640",
                }}
              >
                <MDTypography variant="body2" sx={{ color: "#c62828", lineHeight: 1.5 }}>
                  {error}
                </MDTypography>
              </MDBox>
            )}

            <MDTypography variant="body2" fontWeight="bold" mb={1.5} sx={{ color: palette.brand[600] }}>
              {t("splitAccount.childSectionTitle")}
            </MDTypography>
            <Field
              label={t("formLabels.firstName")}
              value={childFirstName}
              onChange={setChildFirstName}
            />
            <Field
              label={t("formLabels.lastName")}
              value={childLastName}
              onChange={setChildLastName}
            />
            <Field
              label={t("splitAccount.childEmailLabel")}
              value={childEmail}
              onChange={setChildEmail}
              type="email"
              placeholder={t("splitAccount.childEmailPlaceholder")}
            />
            <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: -1.5, mb: 2.5, lineHeight: 1.5 }}>
              {t("splitAccount.childEmailHelp")}
            </MDTypography>

            <MDTypography variant="body2" fontWeight="bold" mb={1.5} sx={{ color: palette.brand[600] }}>
              {t("splitAccount.parentSectionTitle")}
            </MDTypography>
            <Field
              label={t("formLabels.firstName")}
              value={parentFirstName}
              onChange={setParentFirstName}
            />
            <Field
              label={t("formLabels.lastName")}
              value={parentLastName}
              onChange={setParentLastName}
            />

            <MDBox display="flex" gap={2} mt={2}>
              <MDButton
                variant="outlined"
                fullWidth
                onClick={() => setStep("intro")}
                sx={{
                  borderRadius: "12px", py: 1.5, textTransform: "none",
                  borderColor: palette.neutral[300], color: palette.neutral[600], fontWeight: "bold",
                }}
              >
                {t("buttonTitles.back") || "Back"}
              </MDButton>
              <MDButton
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "12px", py: 1.5, textTransform: "none",
                  bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold",
                  "&:hover": { bgcolor: palette.brand[600] },
                }}
              >
                {t("buttonTitles.continue") || "Continue"}
              </MDButton>
            </MDBox>
          </form>
        )}

        {/* ── Step 3: confirm, because this cannot be undone ── */}
        {step === "confirm" && (
          <>
            <MDBox
              mb={2.5}
              sx={{ p: 2, borderRadius: "12px", bgcolor: "#ff980012", border: "1px solid #ff980040" }}
            >
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#e65100", mb: 0.5 }}>
                {t("splitAccount.confirmWarningTitle")}
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "#e65100", lineHeight: 1.6 }}>
                {t("splitAccount.confirmWarningBody")}
              </MDTypography>
            </MDBox>

            <Bullet icon="alternate_email" color={palette.brand[500]}>
              {t("splitAccount.confirmChildEmail", {
                childName: displayChildName,
                email: childEmail.trim().toLowerCase(),
              })}
            </Bullet>
            <Bullet icon="school" color={palette.brand[500]}>
              {t("splitAccount.confirmResultsMove", { childName: displayChildName })}
            </Bullet>
            <Bullet icon="block" color="#ff9800">
              {t("splitAccount.pointNoMoreAssessments")}
            </Bullet>

            <MDBox display="flex" gap={2} mt={3.5}>
              <MDButton
                variant="outlined"
                fullWidth
                disabled={submitting}
                onClick={() => setStep("form")}
                sx={{
                  borderRadius: "12px", py: 1.5, textTransform: "none",
                  borderColor: palette.neutral[300], color: palette.neutral[600], fontWeight: "bold",
                }}
              >
                {t("buttonTitles.back") || "Back"}
              </MDButton>
              <MDButton
                variant="contained"
                fullWidth
                disabled={submitting}
                onClick={submit}
                sx={{
                  borderRadius: "12px", py: 1.5, textTransform: "none",
                  bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold",
                  "&:hover": { bgcolor: palette.brand[600] },
                  "&.Mui-disabled": { bgcolor: palette.neutral[300], color: "#fff" },
                }}
              >
                <span translate="no" className="notranslate">
                  {submitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t("splitAccount.confirmButton")
                  )}
                </span>
              </MDButton>
            </MDBox>
          </>
        )}

        {/* ── Done ── */}
        {step === "done" && (
          <>
            <MDTypography variant="body2" color="text.secondary" mb={2.5} sx={{ lineHeight: 1.7 }}>
              {warnings.includes("child_login_not_created")
                ? t("splitAccount.successBodyNoLogin", {
                    childName: displayChildName,
                    email: childEmail.trim().toLowerCase(),
                  })
                : t("splitAccount.successBody", {
                    childName: displayChildName,
                    email: childEmail.trim().toLowerCase(),
                  })}
            </MDTypography>
            <Bullet icon="login" color="#4caf50">
              {t("splitAccount.successYouStaySignedIn")}
            </Bullet>

            <MDButton
              variant="contained"
              fullWidth
              onClick={close}
              sx={{
                mt: 2.5, borderRadius: "12px", py: 1.5, textTransform: "none",
                bgcolor: palette.brand[500], color: "#fff", fontWeight: "bold",
                "&:hover": { bgcolor: palette.brand[600] },
              }}
            >
              {t("buttonTitles.done") || "Done"}
            </MDButton>
          </>
        )}
      </MDBox>
    </Dialog>
  );
};

export default SplitSharedAccount;
